import { gsap } from "gsap";

import { ScrollTrigger } from "gsap/ScrollTrigger.js";
import Splide from "@splidejs/splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import { generalStore } from "./GeneralStore.js";

gsap.registerPlugin(ScrollTrigger);

export class Animations {
  stickyPosition(elementId) {
    const element = document.getElementById(elementId);
    let scrollTriggerInstance = null;

    const resetInstance = () => {
      scrollTriggerInstance.kill();
      scrollTriggerInstance = null;
    };

    const initSticky = () => {
      if (scrollTriggerInstance) {
        resetInstance();
      }

      scrollTriggerInstance = gsap.to(element, {
        scrollTrigger: {
          trigger: document.documentElement,
          start: "top top",
          end: "+=60",
          scrub: true,
          pin: false,
        },
        top: 30,
        ease: "none",
      });
    };

    generalStore.watch("navOpen", (value) => {
      if (value) {
        const spacer = document.getElementById("nav-menu-spacer");

        gsap.to(spacer, {
          scrollTrigger: {
            trigger: document.documentElement,
            start: "top top",
            end: "+=60",
            scrub: true,
            pin: false,
          },
          height: 0,
          ease: "none",
        });
      }
    });

    if (!generalStore.state.navOpen) {
      initSticky();
    }
  }

  logoCloudsInfiniteScroll() {
    if (document.querySelector(".splide__logos")) {
      let servicesSplide = new Splide(".splide__logos", {
        type: "loop",

        perPage: 3,
        perMove: 1,
        focus: "center",
        arrows: false,
        pagination: false,
        autoScroll: {
          speed: 1,
          pauseOnFocus: false,
          pauseOnHover: false,
        },
        autoWidth: true,
      });

      servicesSplide.mount({ AutoScroll });
    }
  }

  paralaxImage(element) {
    const el = document.querySelector(element);

    if (el) {
      gsap.fromTo(
        el,
        {
          y: Number(el?.dataset.fromY ?? 0),
          ease: "none",
        },
        {
          y: Number(el?.dataset.toY ?? -400),
          ease: "none",
          scrollTrigger: {
            trigger: el,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
          },
        }
      );
    }
  }

  animateStats() {
    const wrapper = document.querySelector(".stats-section");
    const statItems = document.getElementsByClassName("stat-item");

    const statsTl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapper,
        start: "top 50%",
        end: "bottom 20%",
        scrub: false,
        markers: false,
      },
    });

    Array.from(statItems).forEach((item, index) => {
      const iconEl = item.children[0].children[0];
      const numberEl = item.children[1].children[0];
      const labelEl = item.children[1].children[1];

      const start = Number(numberEl.dataset.start || 0);
      const stop = Number(numberEl.dataset.stop);
      const prefix =
        numberEl.dataset.prefix || numberEl.getAttribute("prefix") || "";
      const sufix =
        numberEl.dataset.sufix ||
        numberEl.dataset.suffix ||
        numberEl.getAttribute("sufix") ||
        numberEl.getAttribute("suffix") ||
        "";
      const decimals = Number.isInteger(stop) ? 0 : 1;

      const counterObj = { value: start };

      statsTl
        .to(
          item,
          {
            opacity: 1,
            height: item.dataset.size + "%",
            duration: 1,
            ease: "power3.out",
          },
          index > 0 ? "-=0.9" : ""
        )
        .fromTo(
          iconEl,
          {
            y: 50,
          },
          {
            y: 0,
            opacity: 1,
          },
          "-=0.8"
        )
        .fromTo(
          numberEl,
          {
            x: 50,
          },
          {
            x: 0,
            opacity: 1,
          },
          "-=0.8"
        )
        .to(
          counterObj,
          {
            value: stop,
            duration: 0.5,
            onUpdate: function () {
              numberEl.innerText = `${prefix}${counterObj.value.toFixed(
                decimals
              )}${sufix}`;
            },
          },
          "-=0.8"
        )
        .fromTo(
          labelEl,
          {
            x: -50,
          },
          {
            x: 0,
            opacity: 1,
          },
          "-=0.8"
        );
    });
  }

  animateVerticalStats() {
    const wrapper = document.querySelector(".v-stats-section");
    const statItems = document.getElementsByClassName("v-stat-item");

    const vStatsTl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapper,
        start: "top 75%",
        end: "bottom 20%",
        scrub: false,
        markers: false,
      },
    });

    Array.from(statItems).forEach((item, index) => {
      const iconEl = item.children[0].children[0];
      const numberEl = item.children[1].children[0];
      const labelEl = item.children[1].children[1];

      const start = Number(numberEl.dataset.start || 0);
      const stop = Number(numberEl.dataset.stop);
      const prefix =
        numberEl.dataset.prefix || numberEl.getAttribute("prefix") || "";
      const sufix =
        numberEl.dataset.sufix ||
        numberEl.dataset.suffix ||
        numberEl.getAttribute("sufix") ||
        numberEl.getAttribute("suffix") ||
        "";
      const decimals = Number.isInteger(stop) ? 0 : 1;

      const counterObj = { value: start };

      vStatsTl
        .to(
          item,
          {
            opacity: 1,
            height: item.dataset.size + "%",
            duration: 1,
            ease: "power3.out",
          },
          index > 0 ? "-=0.9" : ""
        )
        .fromTo(
          iconEl,
          {
            y: 50,
          },
          {
            y: 0,
            opacity: 1,
          },
          "-=0.8"
        )
        .fromTo(
          numberEl,
          {
            x: 50,
          },
          {
            x: 0,
            opacity: 1,
          },
          "-=0.8"
        )
        .to(
          counterObj,
          {
            value: stop,
            duration: 0.5,
            onUpdate: function () {
              numberEl.innerText = `${prefix}${counterObj.value.toFixed(
                decimals
              )}${sufix}`;
            },
          },
          "-=0.8"
        )
        .fromTo(
          labelEl,
          {
            x: -50,
          },
          {
            x: 0,
            opacity: 1,
          },
          "-=0.8"
        );
    });
  }

  mouseMoveEffect(el, value = 0.5) {
    const element = document.querySelector(el);

    if (element) {
      document.addEventListener("mousemove", (e) => {
        const x = (e.clientX / window.innerWidth - value) * 200 * -1;
        const y = (e.clientY / window.innerHeight - value) * 200 * -1;

        gsap.to(element, {
          x: x,
          y: y,
          duration: 0.3,
          ease: "power2.out",
        });
      });
    }
  }
}
